import { fabric } from 'fabric';
import { Parts } from './PolicyRect';
import { ProgramPolicy } from '@/types/Programs';
import { DEFAULT_COVERAGE_HEIGHT, DEFAULT_RECT_HEIGHT, MIN_RECT_SIZE } from '@/utilities/tower/v2/Canvas';
import { FONT_SIZES } from '@/types/tower/Tower';
import Store from '@/store';
import LabeledObjectBase from '../LabeledObjectBase';


const CoverageContainer = fabric.util.createClass(LabeledObjectBase, {
  type: Parts.CoverageContainer,

  initialize: function (policy: ProgramPolicy, options: fabric.RectOptions) {
    // default options for the Group
    const defaultOpts = {
      originX: 'center',
      strokeDashArray: [0],
      strokeUniform: true,
      width: MIN_RECT_SIZE * 8,
      height: DEFAULT_COVERAGE_HEIGHT,
      lockScalingFlip: true,
      top: DEFAULT_RECT_HEIGHT - DEFAULT_COVERAGE_HEIGHT,
    };

    const rect = options.objects?.[0];
    const label = options.objects?.[1];

    const rectObj = new fabric.Rect(
      rect
        ? { ...rect }
        : {
            originX: 'center',
            originY: 'center',
            fill: '#A3BAC2',
            opacity: 1,
            strokeDashArray: [0],
            strokeUniform: true,
            width: MIN_RECT_SIZE * 8,
            height: DEFAULT_COVERAGE_HEIGHT,
            lockScalingFlip: true,
            backgroundColor: '#A3BAC2',
          }
    );

    const labelObj = new fabric.Textbox(
      policy.coverageType ?? '',
      label
        ? { ...label }
        : {
            fontSize: FONT_SIZES[1],
            originX: 'center',
            originY: 'center',
            textAlign: 'center',
            fill: 'white',
            strokeDashArray: [0],
            strokeUniform: true,
            lockScalingFlip: true,
          }
    );

    this.callSuper('initialize', [rectObj, labelObj], { ...defaultOpts, ...options });

    this.on('scaling', () => {
      if (this.group) {
        let groupScaleY = this.group.get('scaleY');

        // If the group is nested, consider scale of the parent group (this group is most likely the active selection)
        if (this.group.group) {
          groupScaleY *= this.group.group.get('scaleY');
        }
        // Inverse the Y scaling to prevent rect from scaling with the group
        this.set('scaleY', 1 / groupScaleY);
        const groupHeight = this.group.get('height');
        // reset top to prevent horizontal movement
        this.set('top', ((groupHeight * groupScaleY) / 2 - this.height) / groupScaleY);
      }
    });
  },

  set: function <K extends keyof fabric.CoverageContainer>(
    ...params: [K, fabric.CoverageContainer[K]] | [Record<K, fabric.CoverageContainer[K]>]
  ) {
    // options object
    if (params.length === 1) {
      const options = params[0];
      for (const prop in options) {
        switch (prop) {
          case 'fontFamily': {
            this._objects[1]?.set(prop, options[prop]);
            break;
          }
          case 'coverageContainerStroke':
            this._objects[1]?.set('fill', options[prop]);
            break;
          case 'coverageContainerFill':
            this._objects[0]?.set('fill', options[prop]);
            break;
          case 'stroke':
          case 'fill':
            break;
          default:
            this.callSuper('set', prop, options[prop]);
        }
      }
    }
    // key/value pair
    else if (params.length === 2) {
      const prop = params[0];
      const value = params[1];
      switch (prop) {
        case 'fontFamily': {
          this._objects[1]?.set(prop, value);
          break;
        }
        case 'coverageContainerStroke':
          this._objects[1]?.set('fill', value);
          this._objects[1]?.set('stroke', value);
          break;
        case 'coverageContainerFill':
          this._objects[0]?.set('fill', value);
          break;
        default:
          this.callSuper('set', prop, value);
      }
    }
  },

  resizeLabel() {
    const zoom = Store.state.towerCanvasState.zoomLevel;
    const s = this.getTotalObjectScaling();

    if (this.group) {
      let groupScaleX = this.group.get('scaleX');

      // to account for the scale of the active selection
      if (this.group.group) {
        groupScaleX *= this.group.group.get('scaleX');
      }
      this._objects[1].set('scaleY', 1);
      this._objects[1].set('scaleX', 1 / groupScaleX);
      this._objects[1].set('width', this.group.get('width'));
    } else {
      this._objects[1].set('scaleY', Math.abs((this.height * zoom) / (this.height * s.scaleY)));
      this._objects[1].set('scaleX', Math.abs((this.width * zoom) / (this.width * s.scaleX)));
      this._objects[1].set('width', Math.abs((this.width * s.scaleX) / zoom));
    }
  },

  setLabel(text: string): void {
    this._objects[1].set('text', text);
  },

  toObject() {
    return fabric.util.object.extend(this.callSuper('toObject'), {
      originX: this.originX,
      originY: this.originY,
    });
  },
});

CoverageContainer.fromObject = function (object: fabric.Object, callback: (obj: fabric.Object) => void) {
  const obj = new CoverageContainer(object);
  if (callback) callback(obj);
  return obj;
};

export default CoverageContainer;
